import React, {
  useCallback,
  useMemo,
  useState,
} from "react";

import {
  useSelector,
} from "react-redux";

import "./RecentMedicalVisits.css";

import plusIcon from "../../../../assets/icons/plus.png";

import externalLinkIcon from "../../../../assets/icons/svg/recent-medical-visits-external-link.svg";

import {
  useTranslation,
} from "react-i18next";

import useFileRecentMedicalVisits from "../../../hooks/useFileRecentMedicalVisits";

/* =========================================================
   NORMALIZE
========================================================= */

const normalizeConsultations = (
  value
) => {
  if (!value) {
    return [];
  }

  if (
    Array.isArray(
      value
    )
  ) {
    return value;
  }

  if (
    Array.isArray(
      value?.data
    )
  ) {
    return value.data;
  }

  if (
    Array.isArray(
      value?.items
    )
  ) {
    return value.items;
  }

  if (
    Array.isArray(
      value?.results
    )
  ) {
    return value.results;
  }

  if (
    Array.isArray(
      value?.consultations
    )
  ) {
    return value.consultations;
  }

  if (
    value?.data &&
    typeof value.data ===
      "object"
  ) {
    return normalizeConsultations(
      value.data
    );
  }

  return [];
};

const normalizeDocuments = (value) => {
  const documents = Array.isArray(value)
    ? value
    : Array.isArray(value?.documents)
      ? value.documents
      : Array.isArray(value?.data)
        ? value.data
        : Array.isArray(value?.data?.documents)
          ? value.data.documents
          : value
            ? [value]
            : [];

  return documents
    .map((document) => {
      const description = String(
        document?.description ||
          document?.content ||
          document?.text ||
          ""
      ).trim();

      return description
        ? { ...document, description }
        : document;
    })
    .filter(Boolean);
};

const documentLabel = (document, t) => {
  const type = String(
    document?.documentType ||
      document?.type ||
      "prescription"
  ).toLowerCase();

  const key = {
    bilan: "recentVisits.documentTypes.bilan",
    radio: "recentVisits.documentTypes.radio",
    lettre: "recentVisits.documentTypes.letter",
  }[type] || "recentVisits.documentTypes.prescription";
  return t(key);
};

const translateSectionTitle = (title, t) => {
  const normalized = String(title || "").trim().toLowerCase();
  const key = {
    interrogatoire: "recentVisits.sections.interrogatoire",
    "medical history taking": "recentVisits.sections.interrogatoire",
    "patient history": "recentVisits.sections.interrogatoire",
    "examen clinique": "recentVisits.sections.examenClinique",
    "clinical examination": "recentVisits.sections.examenClinique",
    "physical examination": "recentVisits.sections.examenClinique",
    "examen complémentaire": "recentVisits.sections.examenComplementaire",
    "additional examination": "recentVisits.sections.examenComplementaire",
    "additional tests": "recentVisits.sections.examenComplementaire",
    diagnostic: "recentVisits.sections.diagnostic",
    diagnosis: "recentVisits.sections.diagnostic",
  }[normalized];
  return key ? t(key) : title;
};

const createHistoryTable = (headers, rows) => {
  const visibleRows = rows.filter((row) => row.some((cell) => String(cell ?? "").trim()));
  if (!visibleRows.length) return "";
  return {
    type: "table",
    headers,
    rows: visibleRows,
    searchText: visibleRows.flat().join(" "),
  };
};

const formatHistoryDescription = (description, t, language) => {
  try {
    const snapshot = JSON.parse(description);
    const data = snapshot?.data;
    const changedFields = Array.isArray(snapshot?.changedFields) ? snapshot.changedFields : [];
    if (!data || typeof data !== "object") {
      if (changedFields.length) {
        return createHistoryTable(
          [
            t("recentVisits.history.field", { defaultValue: "Field" }),
            t("recentVisits.history.value", { defaultValue: "Value" }),
          ],
          changedFields.map(({ label, value }) => [label || "—", String(value ?? "")]),
        );
      }
      return description;
    }
    if (!Array.isArray(data) && Object.keys(data).length === 0) return "";
    if (Array.isArray(data) && data.length === 0) return "";

    const tableLabel = (key, fallback) =>
      t(key, { ns: "neurologue", defaultValue: fallback });
    const yesNo = (value) =>
      t(value ? "common.yes" : "common.no", { defaultValue: value ? "Yes" : "No" });
    const formatDate = (value) => {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString(language);
    };
    const list = (values, namespace, keys = true) =>
      (Array.isArray(values) ? values : [])
        .map((value) => keys
          ? t(`${namespace}.${value}`, { ns: "neurologue", defaultValue: value })
          : value)
        .join(", ");

    if (Array.isArray(data) && data.some((item) => item?.imagingType)) {
      const imagingKeys = {
        catheterization: "catheterization",
        ultrasound: "ultrasound",
        "ct-scan": "ctScan",
        "renal-biopsy": "biopsy",
      };
      return createHistoryTable(
        [
          tableLabel("imaging.title", "Imaging"),
          tableLabel("imaging.date", "Date"),
          tableLabel("imaging.report", "Report"),
          tableLabel("imaging.attachment", "Attachment"),
        ],
        data.map((item) => [
          tableLabel(`imaging.${imagingKeys[item.imagingType] || item.imagingType}`, item.imagingType || ""),
          formatDate(item.dateDescription),
          item.description || "",
          item.attachment || "",
        ]),
      );
    }

    if (Array.isArray(data.biologicalParameters)) {
      const parameters = data.biologicalParameters;
      const periodIds = parameters.find((parameter) => Array.isArray(parameter.periods))?.periods || [];
      const maxValues = Math.max(1, ...parameters.map((parameter) =>
        Array.isArray(parameter.values) ? parameter.values.length : 3,
      ));
      const periodHeaders = Array.from({ length: maxValues }, (_, index) => {
        const period = periodIds[index];
        if (period === "current") return tableLabel("biology.current", "Current");
        if (period === "m6") return tableLabel("biology.m6", "M-6");
        if (period === "m3") return tableLabel("biology.m3", "M-3");
        if (maxValues - index === 1) return tableLabel("biology.current", "Current");
        const monthsAgo = (maxValues - index - 1) * 3;
        return t("biology.monthResult", {
          ns: "neurologue",
          count: monthsAgo,
          defaultValue: `M-${monthsAgo}`,
        });
      });
      const rows = parameters.map((parameter) => {
        const nameKey = parameter.parameter === "ckdStage"
          ? "stage"
          : parameter.parameter === "albuminuriaCategory"
            ? "albuminuria"
            : parameter.parameter === "egfr"
              ? "egfrName"
              : parameter.parameter;
        const label = tableLabel(`biology.${nameKey}`, parameter.parameter || "");
        const values = Array.isArray(parameter.values)
          ? parameter.values
          : [parameter.m6, parameter.m3, parameter.current];
        return [
          `${label}${parameter.unit ? ` (${parameter.unit})` : ""}`,
          ...Array.from({ length: maxValues }, (_, index) => values[index] ?? ""),
          parameter.target || "",
        ];
      });
      const epoLabel = tableLabel("biology.epo", "EPO / ESA");
      if (data.epoEsa !== undefined) {
        rows.push([epoLabel, `${yesNo(data.epoEsa)}${data.epoEsaDate ? ` (${formatDate(data.epoEsaDate)})` : ""}`, ...Array(maxValues - 1).fill(""), ""]);
      }
      return createHistoryTable(
        [
          tableLabel("biology.parameter", "Parameter"),
          ...periodHeaders,
          tableLabel("biology.target", "Target"),
        ],
        rows,
      );
    }

    if ("urinarySymptoms" in data || "uremicSigns" in data || "bu" in data || "glycemia" in data) {
      const rows = [];
      const symptoms = list(data.urinarySymptoms, "urine");
      if (symptoms) rows.push([tableLabel("urine.symptoms", "Urinary symptoms"), symptoms]);
      if (data.bm) rows.push([tableLabel("urine.bowel", "Bowel"), data.bm]);
      if (data.bu && typeof data.bu === "object") {
        ["hematuria", "ketones", "glucose", "proteinuria", "leukocyturia"].forEach((key) => {
          if (data.bu[key] !== undefined && data.bu[key] !== null && data.bu[key] !== "") {
            rows.push([tableLabel(`urine.${key}`, key), data.bu[key]]);
          }
        });
        if (data.bu.ph !== undefined && data.bu.ph !== null) rows.push([tableLabel("urine.ph", "pH"), data.bu.ph]);
      }
      const signs = list(data.uremicSigns, "urine");
      if (signs) rows.push([tableLabel("urine.uremicSigns", "Uremic signs"), signs]);
      if (data.glycemia !== undefined && data.glycemia !== null && data.glycemia !== "") {
        rows.push([tableLabel("urine.glycemia", "Glycemia"), data.glycemia]);
      }
      return createHistoryTable(
        [tableLabel("biology.parameter", "Parameter"), tableLabel("biology.result", "Result")],
        rows,
      );
    }

    if ("bsra" in data || "sglt" in data || "finerenone" in data || "nephroAins" in data || "presuppleance" in data || "medicalActs" in data) {
      const rows = [];
      [["bsra", "raas"], ["sglt", "sglt"], ["finerenone", "finerenone"]].forEach(([field, label]) => {
        if (data[field] !== undefined) {
          rows.push([
            tableLabel(`nephro.${label}`, label),
            `${yesNo(data[field])}${data[`${field}Date`] ? ` (${formatDate(data[`${field}Date`])})` : ""}`,
          ]);
        }
      });
      const translatedLists = [
        ["nephroAins", "toxicityReview"],
        ["vaccination", "vaccination"],
        ["medicalActs", "procedures"],
      ];
      translatedLists.forEach(([field, label]) => {
        const values = list(data[field], "nephro");
        if (values) rows.push([tableLabel(`nephro.${label}`, label), values]);
      });
      if (data.posologie?.length) rows.push([tableLabel("nephro.doseAdjustment", "Dose adjustment"), yesNo(true)]);
      const modality = { HD: "hd", DP: "pd", TR: "transplant" }[data.presuppleance];
      if (modality) rows.push([tableLabel("nephro.modality", "Modality"), tableLabel(`nephro.${modality}`, modality)]);
      [["dopplerMs", "doppler"], ["fav", "fistula"], ["capitauxVineux", "venous"]].forEach(([field, label]) => {
        if (data[field] !== undefined) rows.push([tableLabel(`nephro.${label}`, label), yesNo(data[field])]);
      });
      if (data.saltIntake !== undefined && data.saltIntake !== null) {
        rows.push([tableLabel("nephro.saltTarget", "Salt target"), `${data.saltIntake} ${tableLabel("nephro.gDay", "g/day")}`]);
      }
      if (data.proteinTarget !== undefined && data.proteinTarget !== null) {
        rows.push([tableLabel("nephro.proteinTarget", "Protein target"), `${data.proteinTarget} ${tableLabel("nephro.gKgDay", "g/kg/day")}`]);
      }
      return createHistoryTable(
        [tableLabel("biology.parameter", "Parameter"), tableLabel("biology.result", "Result")],
        rows,
      );
    }

    if ("diagnosis" in data || "summary" in data || "plan" in data) {
      return createHistoryTable(
        [tableLabel("biology.parameter", "Section"), tableLabel("biology.result", "Details")],
        [
          [tableLabel("summary.diagnosis", "Diagnosis"), data.diagnosis || ""],
          [tableLabel("summary.total", "Summary"), data.summary || ""],
          [tableLabel("summary.planTitle", "Plan"), data.plan || ""],
        ],
      );
    }

    if ("diseaseHistory" in data || "patientClinicalExamination" in data) {
      const storedReasons = Array.isArray(data.diseaseHistory)
        ? data.diseaseHistory
        : (() => {
            const value = String(data.diseaseHistory || "");
            return value.includes("\n") ? value.split("\n") : value.split(",");
          })();
      const reasons = storedReasons
        .map((reason) => String(reason).trim())
        .filter(Boolean)
        .map((reason) => t(`reason.reasons.${reason}`, { ns: "neurologue", defaultValue: reason }))
        .join(", ");
      return createHistoryTable(
        [tableLabel("biology.parameter", "Field"), tableLabel("biology.result", "Details")],
        [
          [tableLabel("reason.title", "Reason"), reasons],
          [tableLabel("reason.patientHistory", "Patient history and clinical examination"), data.patientClinicalExamination || ""],
        ],
      );
    }

    if (changedFields.length) {
      return createHistoryTable(
        [tableLabel("recentVisits.history.field", "Field"), tableLabel("recentVisits.history.value", "Value")],
        changedFields.map(({ label, value }) => [label || "—", Array.isArray(value) ? value.join(", ") : String(value ?? "")]),
      );
    }

    return createHistoryTable(
      [tableLabel("recentVisits.history.field", "Field"), tableLabel("recentVisits.history.value", "Value")],
      Object.entries(data).map(([key, value]) => [
        key,
        Array.isArray(value) ? value.join(", ") : typeof value === "object" && value !== null
          ? Object.entries(value).map(([nestedKey, nestedValue]) => `${nestedKey}: ${nestedValue}`).join(", ")
          : String(value ?? ""),
      ]),
    ) || "";
  } catch {
    return description;
  }
};

const HistoryDescription = ({ description, className = "cs-paragraph" }) => {
  if (!description) return <p className={className}>—</p>;
  if (typeof description === "string") return <p className={className}>{description}</p>;
  if (description.type !== "table") return <p className={className}>{String(description)}</p>;

  return (
    <div className={`${className} cs-history-table-wrap`}>
      <table className="cs-history-table">
        <thead>
          <tr>{description.headers.map((header, index) => <th key={`${header}-${index}`}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {description.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {description.headers.map((_, cellIndex) => (
                cellIndex === 0
                  ? <th scope="row" key={cellIndex}>{row[cellIndex] || "—"}</th>
                  : <td key={cellIndex}>{row[cellIndex] || "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const normalizeSections = (sections, t, language) => {
  if (
    !Array.isArray(
      sections
    )
  ) {
    return [];
  }

  return sections
    .map((section, index) => {
      const description = String(section?.description || "").trim();
      return {
        title: translateSectionTitle(
          String(section?.title || `Section ${index + 1}`).trim(),
          t
        ),
        description: formatHistoryDescription(description, t, language),
      };
    })
    .filter(
      (section) =>
        section.title ||
        section.description
    );
};

const formatDate = (value, language) => {
  if (!value) {
    return "—";
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(
      value
    );
  }

  return date.toLocaleDateString(
    language || "en",
    {
      day:
        "2-digit",

      month:
        "short",

      year:
        "numeric",
    }
  );
};

/* =========================================================
   TRANSFORM
========================================================= */

const transformConsultationToVisit = (
  consultation,
  index,
  currentDoctor,
  t,
  documents,
  language
) => {
  if (!consultation) {
    return null;
  }

  const consultationId =
    consultation.id ||
    consultation._id ||
    null;

  const sections =
    normalizeSections(
      consultation.sections,
      t,
      language
    );

  const doctorName = (() => {
    if (
      consultation
        ?.doctor
        ?.firstName ||
      consultation
        ?.doctor
        ?.lastName
    ) {
      return `Dr. ${`${consultation.doctor.firstName || ""} ${consultation.doctor.lastName || ""}`.trim()}`;
    }

    if (
      currentDoctor
    ) {
      const name =
        `${currentDoctor.firstName || ""} ${currentDoctor.lastName || ""}`.trim() ||
        currentDoctor.fullName ||
        currentDoctor.name ||
        "";

      if (name) {
        return `Dr. ${name}`;
      }
    }

    return `Dr. ${t(
      "recentVisits.fallback.unknown",
      {
        defaultValue:
          "Unknown",
      }
    )}`;
  })();

  return {
    id:
      consultationId ||
      `consultation-${index}`,

    consultationId,

    rawDate:
      consultation
        .consultationDateTime ||
      consultation
        .createdAt,

    date:
      formatDate(
        consultation
          .consultationDateTime ||
          consultation
            .createdAt,
        language
      ),

    doctor:
      doctorName,

    reasonTitle:
      consultation.title ||
      consultation.mainConcern ||
      t(
        "recentVisits.fallback.reason",
        {
          defaultValue:
            "Consultation",
        }
      ),

    sections,

    documents:
      normalizeDocuments(
        documents
      ),

    viewAllLabel:
      t(
        "recentVisits.actions.viewAll",
        {
          defaultValue:
            "View all",
        }
      ),
  };
};

/* =========================================================
   COMPONENT
========================================================= */

export default function RecentMedicalVisits({
  consultations = [],
  doctorId,
  onAddConsultation,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation();

  const isRTL =
    (
      i18n.language ||
      ""
    ).startsWith(
      "ar"
    );

  const [
    query,
    setQuery,
  ] = useState("");

  const [
    sort,
    setSort,
  ] = useState(
    "desc"
  );

  const [
    openSummary,
    setOpenSummary,
  ] = useState(
    false
  );

  const [
    selectedVisit,
    setSelectedVisit,
  ] = useState(
    null
  );

  const [
    searchOpen,
    setSearchOpen,
  ] = useState(
    false
  );

  /* =======================================================
     CURRENT DOCTOR
     Redux seulement.
  ======================================================= */

  const currentDoctor =
    useSelector(
      (state) =>
        state.doctor?.user ||
        null
    );

  /* =======================================================
     CONSULTATIONS
  ======================================================= */

  const consultationsList =
    useMemo(
      () =>
        normalizeConsultations(
          consultations
        ),
      [
        consultations,
      ]
    );

  const {
    prescriptionsMap,
  } = useFileRecentMedicalVisits(
    consultationsList,
    doctorId
  );

  const allVisits =
    useMemo(() => {
      const values =
        consultationsList
          .map(
            (
              consultation,
              index
            ) =>
              transformConsultationToVisit(
                consultation,
                index,
                currentDoctor,
                t,
                prescriptionsMap[
                  consultation.id ||
                    consultation._id
                ],
                i18n.language
              )
          )
          .filter(Boolean);

      return values.sort(
        (
          a,
          b
        ) => {
          const dateA =
            new Date(
              a.rawDate ||
              0
            ).getTime();

          const dateB =
            new Date(
              b.rawDate ||
              0
            ).getTime();

          return sort ===
            "desc"
            ? dateB -
                dateA
            : dateA -
                dateB;
        }
      );
    }, [
      consultationsList,
      currentDoctor,
      prescriptionsMap,
      i18n.language,
      sort,
      t,
    ]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const visits =
    useMemo(() => {
      const q =
        query
          .trim()
          .toLowerCase();

      if (!q) {
        return allVisits;
      }

      return allVisits.filter(
        (
          visit
        ) => {
          const sectionsText =
            visit.sections
              .flatMap(
                (
                  section
                ) => [
                  section.title,
                  typeof section.description === "string"
                    ? section.description
                    : section.description?.searchText,
                ]
              );

          const documentsText =
            (visit.documents || [])
              .flatMap((document) => [
                documentLabel(document, t),
                document.description,
              ]);

          const text =
            [
              visit.date,
              visit.doctor,
              visit
                .reasonTitle,

              ...sectionsText,
              ...documentsText,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

          return text.includes(
            q
          );
        }
      );
    }, [
      query,
      allVisits,
      t,
    ]);

  /* =======================================================
     DRAWER
  ======================================================= */

  const handleOpenSummary =
    useCallback(
      (
        visit
      ) => {
        setSelectedVisit(
          visit
        );
        setOpenSummary(
          true
        );
      },
      []
    );

  const handleCloseSummary =
    useCallback(
      () => {
        setOpenSummary(
          false
        );

        window.setTimeout(
          () => {
            setSelectedVisit(
              null
            );
          },
          220
        );
      },
      []
    );

  /* =======================================================
     UI
  ======================================================= */

  return (
    <>
      <section
        className="rmv-card"
        dir={
          isRTL
            ? "rtl"
            : "ltr"
        }
      >
        <div className="rmv-header">
          <div className="rmv-header-row-1">
            <h3 className="rmv-title">
              {t(
                "recentVisits.title",
                {
                  defaultValue:
                    "Recent Medical Visits",
                }
              )}
            </h3>

            <div className="rmv-sort">
              <button
                type="button"
                className={`rmv-sort-btn ${
                  sort ===
                  "desc"
                    ? "rmv-sort-btn--active"
                    : ""
                }`}
                aria-label={t("recentVisits.sort.desc")}
                title={t("recentVisits.sort.desc")}
                onClick={() =>
                  setSort(
                    "desc"
                  )
                }
              >
                <span className="rmv-sort-icon rmv-sort-icon--up" />
              </button>

              <button
                type="button"
                className={`rmv-sort-btn ${
                  sort ===
                  "asc"
                    ? "rmv-sort-btn--active"
                    : ""
                }`}
                aria-label={t("recentVisits.sort.asc")}
                title={t("recentVisits.sort.asc")}
                onClick={() =>
                  setSort(
                    "asc"
                  )
                }
              >
                <span className="rmv-sort-icon rmv-sort-icon--down" />
              </button>
            </div>
          </div>

          <div className="rmv-header-row-2">
            <div
              className={`rmv-search ${
                searchOpen
                  ? "rmv-search--open"
                  : "rmv-search--closed"
              }`}
            >
              <button
                type="button"
                className="rmv-search-btn"
                aria-label={t("recentVisits.search.open")}
                title={t("recentVisits.search.open")}
                onClick={() => {
                  setSearchOpen(
                    (
                      previous
                    ) =>
                      !previous
                  );

                  setTimeout(
                    () =>
                      document
                        .querySelector(
                          ".rmv-search-input"
                        )
                        ?.focus(),
                    0
                  );
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 25 25"
                  fill="none"
                >
                  <path
                    d="M21.5 21.5L17.16 17.16"
                    stroke="#546881"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />

                  <path
                    d="M11.5 19.5a8 8 0 1 0 0-16a8 8 0 0 0 0 16Z"
                    stroke="#546881"
                    strokeWidth="1.4"
                  />
                </svg>
              </button>

              <input
                className="rmv-search-input"
                value={
                  query
                }
                placeholder={t(
                  "recentVisits.search.placeholder",
                  {
                    defaultValue:
                      "Search...",
                  }
                )}
                onChange={(
                  event
                ) =>
                  setQuery(
                    event.target
                      .value
                  )
                }
                onBlur={() => {
                  if (
                    !query
                  ) {
                    setSearchOpen(
                      false
                    );
                  }
                }}
              />
            </div>

            {onAddConsultation && (
              <button
                className="rmv-add-btn"
                type="button"
                onClick={
                  onAddConsultation
                }
              >
                <img
                  src={
                    plusIcon
                  }
                  alt=""
                  className="rmv-add-icon"
                />

                <span className="rmv-btn-text">
                  {t(
                    "recentVisits.actions.addConsultation",
                    {
                      defaultValue:
                        "Add consultation",
                    }
                  )}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* ================================================
            VISITS
        ================================================ */}

        <div className="rmv-list">
          {visits.map(
            (
              visit
            ) => (
              <article
                key={
                  visit.id
                }
                className="rmv-visit"
              >
                <div className="rmv-meta">
                  <div className="rmv-meta-left">
                    <span className="rmv-meta-text">
                      {
                        visit.date
                      }
                    </span>
                  </div>

                  <div className="rmv-meta-right">
                    <span className="rmv-meta-text">
                      {
                        visit.doctor
                      }
                    </span>
                  </div>
                </div>

                <div className="rmv-reason">
                  <span className="rmv-reason-label">
                    {t(
                      "recentVisits.labels.reason",
                      {
                        defaultValue:
                          "Reason",
                      }
                    )}
                  </span>

                  <span className="rmv-reason-value">
                    {
                      visit.reasonTitle
                    }
                  </span>
                </div>

                {/* ========================================
                    DYNAMIC SECTIONS
                ======================================== */}

                <div className="rmv-sections-scroll">
                  {visit.sections.map(
                    (
                      section,
                      index
                    ) => (
                      <div
                        key={`${visit.id}-${index}`}
                        className="rmv-section-item"
                      >
                        <div className="rmv-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M4 4h16v16H4z"
                              stroke="#006eea"
                              strokeWidth="1.6"
                            />

                            <path
                              d="M8 9h8M8 13h8M8 17h5"
                              stroke="#006eea"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                          </svg>

                          <span className="rmv-section-title">
                            {
                              section.title
                            }
                          </span>
                        </div>

                        {React.createElement(HistoryDescription, {
                          description: section.description,
                          className: "rmv-section-content",
                        })}
                      </div>
                    )
                  )}
                </div>

                <div className="rmv-footer">
                  <button
                    type="button"
                    className="rmv-viewall"
                    onClick={() =>
                      handleOpenSummary(
                        visit
                      )
                    }
                  >
                    {
                      visit.viewAllLabel
                    }

                    <span className="rmv-ext">
                      <img
                        src={
                          externalLinkIcon
                        }
                        alt=""
                        width="13"
                        height="13"
                      />
                    </span>
                  </button>
                </div>
              </article>
            )
          )}

          {visits.length ===
            0 && (
            <div className="rmv-empty">
              {t(
                "recentVisits.empty",
                {
                  defaultValue:
                    "No visits found.",
                }
              )}
            </div>
          )}
        </div>
      </section>

      {/* ================================================
          OVERLAY
      ================================================ */}

      <div
        className={`cs-overlay ${
          openSummary
            ? "is-open"
            : ""
        }`}
        onClick={
          handleCloseSummary
        }
      />

      {/* ================================================
          DRAWER
      ================================================ */}

      <aside
        className={`cs-drawer ${
          openSummary
            ? "is-open"
            : ""
        }`}
        dir={
          isRTL
            ? "rtl"
            : "ltr"
        }
        onClick={
          (event) =>
            event.stopPropagation()
        }
      >
        <div className="cs-topbar">
          <button
            type="button"
            className="cs-back"
            onClick={
              handleCloseSummary
            }
          >
            <span className="cs-back-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 19L5 12L12 5"
                  stroke="#1e1e1e"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />

                <path
                  d="M19 12H5"
                  stroke="#1e1e1e"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
            </span>

            <span>
              {t(
                "recentVisits.actions.back",
                {
                  defaultValue:
                    "Back",
                }
              )}
            </span>
          </button>

          <div className="cs-meta">
            <span className="rmv-meta-item">
              {
                selectedVisit
                  ?.date ||
                "—"
              }
            </span>

            <span className="cs-meta-sep">
              |
            </span>

            <span className="rmv-meta-item">
              {
                selectedVisit
                  ?.doctor ||
                "—"
              }
            </span>
          </div>
        </div>

        <div className="cs-titlewrap">
          <h2 className="rmv-title">
            {t(
              "recentVisits.drawer.title",
              {
                defaultValue:
                  "Consultation Summary",
              }
            )}
          </h2>
        </div>

        <div className="cs-body">
          <div className="cs-section">
            <h4 className="cs-h4">
              {t("recentVisits.labels.reasonTitle")}
            </h4>

            <p className="cs-paragraph">
              {
                selectedVisit
                  ?.reasonTitle ||
                "—"
              }
            </p>

            <div className="rmv-divider" />
          </div>

          {(selectedVisit?.documents || []).map((document, index) => (
            <div className="cs-section" key={`document-${index}`}>
              <h4 className="cs-h4">
                {documentLabel(document, t)}
              </h4>

              <p className="cs-paragraph">
                {document.description || "—"}
              </p>

              <div className="rmv-divider" />
            </div>
          ))}

          {selectedVisit
            ?.sections
            ?.map(
              (
                section,
                index
              ) => (
                <div
                  key={`drawer-${index}`}
                  className="cs-section"
                >
                  <h4 className="cs-h4">
                    {
                      section.title
                    }
                  </h4>

                  {React.createElement(HistoryDescription, {
                    description: section.description,
                  })}

                  <div className="rmv-divider" />
                </div>
              )
            )}
        </div>
      </aside>
    </>
  );
}